<script setup lang="ts">
import { defineProps, ref } from "vue";
import { Pillar } from "@/types/calculators";
import { useI18n } from "vue-i18n";
import ModalComponent from "@/components/ModalComponent.vue";

defineProps<{ pillars: Record<string, Pillar> }>();

const { t } = useI18n();
const hiddenType = ref<"hour" | "day" | "month" | "year" | null>(null);
</script>

<template>
  <div class="flex w-full gap-2">
    <div
      class="bg-[#FDFDFD] rounded-xl flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'hour'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ pillars["hour"].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.hour") }}
      </span>
    </div>

    <div
      class="bg-[#FDFDFD] rounded-xl flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'day'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ pillars["day"].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.day") }}
      </span>
    </div>

    <div
      class="bg-[#FDFDFD] rounded-xl flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'month'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ pillars["month"].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.month") }}
      </span>
    </div>

    <div
      class="bg-[#FDFDFD] rounded-xl flex flex-col items-center py-2 gap-4 flex-1"
      @click="hiddenType = 'year'"
    >
      <div
        class="size-11 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
      >
        {{ pillars["year"].stem }}
      </div>
      <span class="text-base leading-[20px]">
        {{ t("calculators.bazi.results.birthday-map.times.year") }}
      </span>
    </div>

    <ModalComponent
      :show="hiddenType !== null"
      variant="fullscreen"
      @close="hiddenType = null"
      container-class="bg-beige"
    >
      <template #header>
        <p class="font-semibold text-2xl text-black">
          {{ t(`calculators.bazi.modal.natal.title.${hiddenType}`) }}
        </p>
      </template>
      <template #content>
        <div
          class="flex flex-col gap-6 h-full overflow-y-auto bg-white rounded-lg p-4"
        >
          <!-- STEM -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{ pillars[hiddenType].stem }}
              {{ t("calculators.bazi.results.birthday-map.stem.heaven-stem") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ pillars[hiddenType].labels.stem }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ pillars[hiddenType].tips.stem }}
            </p>
          </div>

          <!-- BRANCH -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{ pillars[hiddenType].branch }}
              {{ t("calculators.bazi.results.birthday-map.stem.earth-branch") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ pillars[hiddenType].labels.branch }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ pillars[hiddenType].tips.stem }}
            </p>
          </div>
          <!-- STAGE -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{ pillars[hiddenType].stage }}
              {{ t("calculators.bazi.results.birthday-map.stem.stage") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ pillars[hiddenType].labels.stage }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ pillars[hiddenType].tips.stage }}
            </p>
          </div>
          <!-- ELEMENT -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{
                t(
                  `calculators.bazi.modal.natal.element.${pillars[hiddenType].element}`
                )
              }}
            </p>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ pillars[hiddenType].tips.element }}
            </p>
          </div>
          <!-- GOD -->
          <div class="flex flex-col gap-4">
            <p class="font-semibold text-2xl text-black text-left">
              {{ pillars[hiddenType].god }}
              {{ t("calculators.bazi.results.birthday-map.stem.god") }}
            </p>
            <h5
              class="font-semibold text-xl leading-[24px] text-black text-left"
            >
              {{ pillars[hiddenType].labels.god }}
            </h5>
            <p class="text-left text-base leading-[20px] text-brown">
              {{ pillars[hiddenType].tips.god }}
            </p>
          </div>
        </div>
      </template>
    </ModalComponent>
  </div>
</template>
